/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { AppRouteInterface, RouteInterface } from '../../types/routes';
import Nav from './nav';
import { ReportingLevelDropdown } from './ReportingLevelDropdown';
import { Tree } from '../../types/tree';
import SimpleTooltip from '../simple-tooltip';
import { Button } from 'reactstrap';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useAppSelector } from '../../reducers';
import { generateUrl } from '../../routes/util';
import { ROUTES } from '../../constants/routes';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { useIsPortfolioTracker } from '../../hooks/useIsPortfolioTracker';
import { AppConfig } from '../../types/app';

interface Props {
  appRoute: AppRouteInterface
  appConfig?: AppConfig;
  route: RouteInterface,
  mapRoute?: RouteInterface;
  initiativeTree?: Tree;
}

export const AppHeader = ({ initiativeTree, mapRoute, appRoute, appConfig, route }: Props) => {

  const history = useHistory();
  const limitReportingLevels = useAppSelector(FeaturePermissions.getLimitReportingLevels);
  const canAccessReportingLevels = limitReportingLevels > 1;

  const { initiativeId = '', portfolioId = '' } = useParams<{ initiativeId?: string, portfolioId?: string }>();

  const isPortfolioTracker = useIsPortfolioTracker();
  const navigateByMapRoute = mapRoute ?? ROUTES.NAVIGATE_BY_MAP;

  const matchedRoute = useRouteMatch(navigateByMapRoute);

  if (!appRoute) {
    return null;
  }

  const hasSidebar = appRoute.hasSidebar !== false;
  const onClick = () => {
    if (matchedRoute) {
      // org map is already open, exit
      return;
    }
    history.push(generateUrl(navigateByMapRoute, { initiativeId, portfolioId }));
  };

  return (
    <div className={`monitoring-apps-menu-container ${hasSidebar ? 'has-sidebar' : ''}`}>
      <section className='monitoring-apps-menu d-flex flex-column flex-md-row align-items-md-end'>

        <div className='app-nav-container flex-fill order-2 order-md-1 d-flex'>
          <div className='app-info-icon text-md-right align-self-center'>
            <img src={appConfig?.logo ?? appRoute.appIcon} alt={appRoute.appName} width={150} />
          </div>
          <div className='ml-3 align-self-end'>
            <Nav appRoute={appRoute} />
          </div>
        </div>
        <div className='d-flex flex-column justify-content-end order-1 order-md-2 align-self-center'>
          <div className='reporting-level-dropdown pb-0 d-flex'>
            {appRoute.hasReportingLevels ?
              <>
                <div className={'ml-auto mr-1'}>
                  <SimpleTooltip text='Navigate reporting levels using a visual map'>
                    {isPortfolioTracker ? (
                      <Button
                        className='navigate-by-map-button text-nowrap border-ThemeBorderDefault p-2'
                        outline
                        onClick={onClick}
                      >
                        <i className={`fa-light ${navigateByMapRoute.icon} text-md`} />
                      </Button>
                    ) : (
                      <Button
                        className='navigate-by-map-button text-nowrap'
                        color={!canAccessReportingLevels ? 'link-secondary' : 'transparent'}
                        onClick={onClick}
                      >
                        <i className={`fas ${navigateByMapRoute.icon} mr-1`} />
                        {!canAccessReportingLevels ? 'Org map' : ''}
                      </Button>
                    )}
                  </SimpleTooltip>
                </div>
                <ReportingLevelDropdown initiativeTree={initiativeTree} route={route} />
              </>
              : null
            }
          </div>
        </div>
      </section>
    </div>
  )
}
